import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { ShowToast } from "../../components/toast";
import type {
  CampaignRegenerationResult,
  CampaignRegenerationState,
  CompetitorAdsPlan,
  DraftedKeyword,
  KeywordPushResult,
} from "../../types/competitors";

export const competitorKeywordsKey = (userId: string) => [
  "competitor-keywords",
  userId,
];

export const campaignRegenerationKey = (userId: string) => [
  "campaign-regeneration",
  userId,
];

const GENERATION_POLL_MS = 10_000;

/** Whether the campaigns can be rebuilt from the saved answers, and whether
 *  a rebuild is running. Polls while one is. */
export function useCampaignRegeneration(userId: string) {
  return useQuery<CampaignRegenerationState>({
    queryKey: campaignRegenerationKey(userId),
    queryFn: () => api.semProvisioning.campaignRegeneration(userId),
    enabled: !!userId,
    refetchOnWindowFocus: false,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.generating ? GENERATION_POLL_MS : false,
  });
}

/** Rebuilds the campaigns from the saved answers; the backend removes only
 *  campaigns that never served and republishes through the pipeline. */
export function useRegenerateCampaigns(userId: string) {
  const queryClient = useQueryClient();
  return useMutation<CampaignRegenerationResult, any, void>({
    mutationFn: () => api.semProvisioning.regenerateCampaigns(userId),
    onSuccess: (result) => {
      ShowToast({ type: "success", message: result.message });
      queryClient.invalidateQueries({
        queryKey: campaignRegenerationKey(userId),
      });
      queryClient.invalidateQueries({ queryKey: competitorKeywordsKey(userId) });
    },
    onError: (error) => {
      ShowToast({
        type: "error",
        message:
          error?.response?.data?.message ?? "Failed to regenerate campaigns",
      });
    },
  });
}

/** Which saved competitors already have a keyword in the hotel's live
 *  Competitor Conquesting ad group. Read-only against Google Ads. */
export function useCompetitorAdsPlan(userId: string) {
  return useQuery<CompetitorAdsPlan>({
    queryKey: competitorKeywordsKey(userId),
    queryFn: () => api.semProvisioning.competitorKeywords(userId),
    enabled: !!userId,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    retry: false,
  });
}

/** Drafts keywords for the named competitors — a model call on the backend,
 *  nothing written to Google Ads. */
export function useDraftCompetitorKeywords(userId: string) {
  return useMutation<DraftedKeyword[], any, string[]>({
    mutationFn: (competitorNames) =>
      api.semProvisioning
        .draftCompetitorKeywords(userId, competitorNames)
        .then((result: { keywords: DraftedKeyword[] }) => result.keywords),
    onError: (error) => {
      ShowToast({
        type: "error",
        message:
          error?.response?.data?.message ?? "Failed to draft competitor keywords",
      });
    },
  });
}

function pushSummary(result: KeywordPushResult): string {
  const parts = [
    result.created.length > 0 && `${result.created.length} added`,
    result.skipped.length > 0 && `${result.skipped.length} already live`,
    result.failed.length > 0 && `${result.failed.length} rejected`,
  ].filter(Boolean);
  return `Google Ads: ${parts.join(", ") || "nothing to add"}`;
}

/** Adds the reviewed keywords to the live ad group. */
export function usePushCompetitorKeywords(userId: string) {
  const queryClient = useQueryClient();
  return useMutation<KeywordPushResult, any, DraftedKeyword[]>({
    mutationFn: (keywords) =>
      api.semProvisioning.pushCompetitorKeywords(userId, keywords),
    onSuccess: (result) => {
      ShowToast({
        type: result.failed.length > 0 ? "error" : "success",
        message: pushSummary(result),
      });
      queryClient.invalidateQueries({ queryKey: competitorKeywordsKey(userId) });
    },
    onError: (error) => {
      ShowToast({
        type: "error",
        message:
          error?.response?.data?.message ?? "Failed to push keywords to Google Ads",
      });
    },
  });
}
